'use client'

import { useForm } from 'react-hook-form'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import {
  RangeInputs,
  useRangeFieldErrors,
} from '~/app/accounting/filters/RangeInputs'
import { usdRangeLabel } from '~/app/accounting/filters/helpers'

export type UnitCostFilterValue = { min: string; max: string }

export const EMPTY_UNIT_COST_FILTER: UnitCostFilterValue = {
  min: '',
  max: '',
}

const isEmpty = (value: UnitCostFilterValue) =>
  value.min === '' && value.max === ''

// Bounds on what a lot paid per whole token, which is how someone looks for
// the tokens they bought at a high price regardless of position size.
export const UnitCostFilterButton = ({
  value,
  onChange,
}: {
  value: UnitCostFilterValue
  onChange: (value: UnitCostFilterValue) => void
}) => {
  const form = useForm<UnitCostFilterValue>({ values: value })
  const { min, max } = form.watch()

  const { minError, maxError } = useRangeFieldErrors({
    min,
    max,
    setError: form.setError,
    clearErrors: form.clearErrors,
  })

  const hasChanged = min !== value.min || max !== value.max

  const activeLabel = usdRangeLabel(value)

  const clear = () => {
    form.reset(EMPTY_UNIT_COST_FILTER)
    onChange(EMPTY_UNIT_COST_FILTER)
  }

  return (
    <FilterPopoverButton
      label="Unit cost"
      activeLabel={activeLabel}
      isAppliedEmpty={isEmpty(value)}
      isLocalEmpty={isEmpty({ min, max })}
      isInvalid={!!minError || !!maxError}
      hasChanged={hasChanged}
      onApply={() => onChange({ min, max })}
      onReset={clear}
      onClear={clear}
      onClose={() => {
        if (hasChanged) form.reset(value)
      }}
    >
      {(focusAction) => (
        <RangeInputs
          control={form.control}
          minName="min"
          maxName="max"
          placeholder="$0"
          prefix="$"
          decimalScale={2}
          onComplete={focusAction}
        />
      )}
    </FilterPopoverButton>
  )
}
